import { useSCA } from '@/providers/sca/useSCA'
import { SCACancelledError } from '@/utils/scaErrors'

type FetchWithSCAOptions = RequestInit & {
  requireSCA?: boolean
}

/**
 * Reads the SCA session code out of a 403 body, if it carries one.
 *
 * Clones the response so the caller's body stays unread: only an SCA challenge produces a
 * session code, and every other 403 must still reach the caller with a readable body.
 */
const readSCASessionCode = async (response: Response): Promise<string | null> => {
  try {
    const body = await response.clone().json()
    return body?.statusCode === 403 && body?.info?.scaSessionCode ? body.info.scaSessionCode : null
  } catch {
    return null
  }
}

/**
 * React hook that returns a fetch wrapper with Strong Customer Authentication (SCA) handling.
 *
 * Deliberately minimal. Airwallex's SDK already holds the SCA token and its refresh token,
 * keyed per connected account, so this layer keeps no token state of its own — it asks the
 * SDK each time and lets the SDK decide whether that means a cached read, a silent refresh,
 * or nothing at all.
 *
 * Flow:
 * 1. Make the request
 * 2. If it comes back 403 with a scaSessionCode, ask the SDK for a token
 * 3. Retry once with it
 * 4. If that fails, show the modal for user verification
 *
 * Usage:
 * ```ts
 * const { fetchWithSCA } = useFetchWithSCA()
 * const response = await fetchWithSCA('/api/endpoint', { requireSCA: true })
 * ```
 */
export const useFetchWithSCA = () => {
  const { requestSCA, getSCAToken, getSCAFlowGeneration } = useSCA()

  const withSCAToken = (options: RequestInit, token: string): RequestInit => {
    const headers = new Headers(options.headers)
    headers.set('x-sca-token', token)
    return { ...options, headers }
  }

  const fetchWithSCA = async (url: string, options: FetchWithSCAOptions = {}): Promise<Response> => {
    const { requireSCA = true, ...fetchOptions } = options

    const response = await fetch(url, fetchOptions)

    if (response.status !== 403 || !requireSCA) {
      return response
    }

    try {
      const scaSessionCode = await readSCASessionCode(response)

      // Not an SCA challenge — hand the untouched response back to the caller.
      if (!scaSessionCode) {
        return response
      }

      const flowGeneration = getSCAFlowGeneration()

      const token = await getSCAToken()

      if (token) {
        const retryResponse = await fetch(url, withSCAToken(fetchOptions, token))

        if (retryResponse.ok) {
          return retryResponse
        }
        // The SDK's token was rejected — fall through to user verification.
      }

      if (flowGeneration < getSCAFlowGeneration()) {
        throw new SCACancelledError()
      }

      // No usable token — show the modal for user verification.
      return await new Promise((resolve, reject) => {
        requestSCA(
          scaSessionCode,
          {
            onToken: async (scaToken: string) => {
              const retryResponse = await fetch(url, withSCAToken(fetchOptions, scaToken))
              resolve(retryResponse)
            },
            onCancel: (err?: Error) => {
              // Reject with a real Error so callers get a typed rejection they can handle.
              // An undefined rejection produces an unhandled-rejection warning and makes
              // catch blocks harder to reason about.
              reject(err ?? new SCACancelledError())
            },
          },
          flowGeneration,
        )
      })
    } catch (error) {
      if (error instanceof SCACancelledError) {
        throw error
      }

      console.warn('SCA handling failed for a 403 response:', error)
      return response
    }
  }

  return { fetchWithSCA }
}
